import { observer } from 'mobx-react-lite';

import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SaveableField } from '@/components/ui/SaveableField';
import { cn } from '@/lib/utils';

import { useNotesTextEditor } from './use-notes-text-editor';

type NotesTextAreaProps = {
  savedText: string;
  className?: string;
  wrapperClassName?: string;
};

/** The whole-text editor of a store kept as text, as Settings showed it before items */
export const NotesTextArea = observer(function NotesTextArea({
  savedText,
  className,
  wrapperClassName,
}: NotesTextAreaProps) {
  const editor = useNotesTextEditor(savedText);
  const atCap = editor.text.length >= editor.maxLength;

  return (
    <YStack className={cn('w-full', wrapperClassName)}>
      {editor.changedWhileEditing && (
        <XStack
          role="alert"
          className="mt-2 items-center justify-between gap-3 rounded-lg border border-border-strong bg-surface-hover px-3 py-2 text-sm text-foreground"
        >
          <span>
            Your notes changed while you were editing. Reload to see the current
            notes.
          </span>
          <Button variant="outline" size="dense" onClick={editor.reload}>
            Reload
          </Button>
        </XStack>
      )}
      <SaveableField
        id="chat-instructions"
        className={className}
        wrapperClassName={wrapperClassName}
        placeholder="Edit Littlebird's memory"
        value={editor.text}
        dirty={editor.dirty}
        busy={editor.isSaving}
        saveDisabled={editor.changedWhileEditing}
        onChange={editor.setText}
        onSave={editor.save}
        onDiscard={editor.discard}
        statusContent={
          <XStack className="gap-2 items-center">
            <span className="text-xs text-foreground-muted">
              {editor.text.length}/{editor.maxLength} characters
            </span>
            {atCap && (
              <span className="text-xs text-red-500">
                Character limit reached
              </span>
            )}
            {editor.dirty && (
              <span className="text-xs text-foreground-muted">
                · Unsaved changes
              </span>
            )}
          </XStack>
        }
      />
    </YStack>
  );
});
